import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import { ClientConfig } from "../../../model/client/ClientSettings";
import { ClientSettingUpdate } from "../../../model/client/UpdateClientSettingsRequest";

// Shared load/save plumbing for the JSON-config settings panels (joining fee,
// forms, banners, check-in). Each panel supplies an `extract` that maps the
// loaded /config into its own editable value, and saves via the generic
// updateSettings endpoint. Mirrors the load/dirty/save/error lifecycle of
// ThemeSectionPanel without the theme-specific history dropdown.
export function useJsonSection<T>(extract: (config: ClientConfig | undefined) => T) {
    const service = useClientConfigService();
    const { clientId: param } = useParams<{ clientId: string }>();
    const clientId = param !== undefined ? Number(param) : NaN;

    const [initial, setInitial] = useState<T | null>(null);
    const [value, setValue] = useState<T | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const reload = useCallback(async () => {
        if (!Number.isFinite(clientId)) {
            setLoadError('Invalid client id in URL.');
            return;
        }
        setLoadError(null);
        try {
            const config = await service.getConfig(clientId);
            const v = extract(config);
            setInitial(v);
            setValue(v);
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : 'Failed to load settings.');
        }
    }, [service, clientId, extract]);

    useEffect(() => { reload(); }, [reload]);

    const save = useCallback(async (rows: ClientSettingUpdate[], nextValue: T) => {
        if (!Number.isFinite(clientId)) return;
        setSaving(true);
        setSaveError(null);
        try {
            await service.updateSettings(clientId, { settings: rows });
            setInitial(nextValue);
            setValue(nextValue);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save settings.');
        } finally {
            setSaving(false);
        }
    }, [service, clientId]);

    return { clientId, initial, value, setValue, loadError, saveError, saving, save, reload };
}
